/**
 * Abstract base class for storage backends.
 * All storage implementations must implement these methods.
 */

import { extractVariables as sharedExtractVariables } from '../../shared/index.js';
import type {
  ListFilesResult,
  ReadFileResult,
  ReadComponentResult,
  SaveResult,
  CreateResult,
  DeleteResult,
  RenameResult,
  SnapshotMetadata,
  Snapshot,
  SnapshotReason,
  CreateSnapshotResult,
  RestoreSnapshotResult,
  StorageMetadata,
  VariablesResult,
  MediaItem,
  MediaUploadResult,
  MediaUsage,
  MediaListResult,
  MediaDeleteResult,
  ProjectFileContent,
} from './types.js';

export abstract class StorageBackend {
  readonly name: string;

  constructor(name: string) {
    this.name = name;
  }

  /**
   * List all source files (.src.md) and components
   */
  abstract listFiles(): Promise<ListFilesResult>;

  /**
   * Read a file's content and extract variables
   */
  abstract readFile(path: string): Promise<ReadFileResult>;

  /**
   * Read a component's content
   */
  abstract readComponent(name: string): Promise<ReadComponentResult>;

  /**
   * Raw content of every file and component in the current project
   * (components prefixed with `_components/`). Used for project-wide search.
   */
  abstract getAllContents(): Promise<ProjectFileContent[]>;

  /**
   * Save file content
   */
  abstract saveFile(path: string, content: string): Promise<SaveResult>;

  /**
   * Create a new file
   */
  abstract createFile(path: string, content?: string): Promise<CreateResult>;

  /**
   * Create a new component
   */
  abstract createComponent(name: string, content?: string): Promise<CreateResult>;

  /**
   * Delete a file
   */
  abstract deleteFile(path: string): Promise<DeleteResult>;

  /**
   * Delete a component
   */
  abstract deleteComponent(name: string): Promise<DeleteResult>;

  /**
   * Rename a file
   */
  abstract renameFile(oldPath: string, newPath: string): Promise<RenameResult>;

  /**
   * Rename a component
   */
  abstract renameComponent(oldName: string, newName: string): Promise<RenameResult>;

  /**
   * Check if backend is available/connected
   */
  abstract isAvailable(): Promise<boolean>;

  // ==================== File-Level History/Versioning ====================

  /**
   * Create a snapshot of a specific file's content
   */
  abstract createSnapshot(filePath: string, reason: SnapshotReason): Promise<CreateSnapshotResult>;

  /**
   * List all snapshots for a specific file
   */
  abstract listSnapshots(filePath: string): Promise<SnapshotMetadata[]>;

  /**
   * Get a specific snapshot with full content
   */
  abstract getSnapshot(filePath: string, snapshotId: string): Promise<Snapshot | null>;

  /**
   * Delete a specific snapshot
   */
  abstract deleteSnapshot(filePath: string, snapshotId: string): Promise<DeleteResult>;

  /**
   * Check if a snapshot should be created for a file based on time-based rules
   */
  abstract maybeCreateSnapshot(filePath: string): Promise<void>;

  /**
   * Restore a file to a specific snapshot version
   */
  abstract restoreSnapshot(filePath: string, snapshotId: string): Promise<RestoreSnapshotResult>;

  // ==================== Media/Image Management ====================

  /**
   * Upload a media file (image, etc.)
   * Returns markdown syntax to insert
   */
  abstract uploadMedia(file: File, uploadedBy?: string): Promise<MediaUploadResult>;

  /**
   * List all media files in the current project
   */
  abstract listMedia(): Promise<MediaListResult>;

  /**
   * Get usage information for a media file
   */
  abstract getMediaUsage(filename: string): Promise<MediaUsage>;

  /**
   * Delete a media file
   */
  abstract deleteMedia(filename: string): Promise<MediaDeleteResult>;

  /**
   * Bulk delete media files
   */
  abstract bulkDeleteMedia(filenames: string[]): Promise<MediaDeleteResult>;

  /**
   * Get the URL for a media file (for rendering)
   */
  abstract getMediaUrl(filename: string): string;

  // ==================== Checklist Mode ====================

  /**
   * Get checked heading slugs for a file.
   * Returns null if no checklist exists (mode off), or string[] (mode on).
   */
  abstract getChecklist(filePath: string): Promise<string[] | null>;

  /**
   * Save checked heading slugs for a file (creates or updates).
   */
  abstract saveChecklist(filePath: string, checkedSlugs: string[]): Promise<void>;

  /**
   * Delete all checklist data for a file.
   */
  abstract deleteChecklist(filePath: string): Promise<void>;

  /**
   * Get backend-specific metadata
   */
  async getMetadata(): Promise<StorageMetadata> {
    return { name: this.name };
  }

  /**
   * Extract variables from frontmatter content
   * Uses shared extractVariables from ../../shared/variables-utils.ts
   */
  extractVariables(content: string): VariablesResult {
    return sharedExtractVariables(content);
  }
}
